import React, { useMemo, useRef, useState } from 'react'
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { Badge, Tabs, Checkbox, Dropdown } from "antd";
import { CircleX, EllipsisVertical, ThumbsUp, Check, X, ChevronRight } from "lucide-react";
import TalentDisplayName from "../../components/TalentDisplayName";
import CandidateActions from "./CandidateActions";
import HideTalentPricingTooltip from "../clients/HideTalentPricingTooltip";
import ClientPreferredRateModal from "./ClientPreferredRateModal";
import { UserImageCircleNEW } from "../../components/UserImageCircle";
import { useAuth } from "../../providers/AuthProvider";
import { useCandidate } from "../../providers/CandidateProvider";
import { usePosition } from '../../providers/ProjectPositionProvider';
import { useProject } from '../../providers/ClientProjectProvider';
import TalentMatchDetails from "./TalentMatchDetails";
import CandidateHireConfidence from "./CandidateHireConfidence";
import CandidateRoleMatch from "./CandidateRoleMatch";
import useSharedFitTab from "./useSharedFitTab";


const CandidateTileRedesign = ({ children, ...props }) => {
    const { auth } = useAuth();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const { project } = useProject()
    const { position } = usePosition();
    const [activeTab, setActiveTab, canSeeHireConfidence] = useSharedFitTab();
    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        showCandidateProposal,
        matchingDisciplines,
        canArchive,
        onAddToBulkActions,
        isBulkActionsSelected,
        canRunBulkActions,
        removeFromArchive,
        addToArchive,
    } = useCandidate();

    const isBrowsePage = location.pathname?.includes('browse');
    const isShortlistPage = location.pathname?.includes('bidding') && location.pathname?.includes('shortlist') || (location.pathname?.includes('active') && location.pathname?.includes('short-list'));
    const isTalentPoolPage = location.pathname?.includes('bidding') && location.pathname?.includes('talent-pool');

    const profileLink = useMemo(() => {
        let params = new URLSearchParams(searchParams);
        if (position) {
            params.set('role', position?.id);
        }
        return {
            pathname: `talent-profiles/${candidate.id}`,
            search: params.size > 0 ? `?${params.toString()}` : undefined
        }
    }, [position?.id, searchParams?.toString()])

    const onCandidateAction = async (request) => {
        const update = await props.onRequest(request);
        if (update && props.onUpdated) {
            await props.onUpdated(update);
        }
    }

    // The role's criteria, as both match surfaces need them: the full
    // breakdown modal below and the Role Match modal on the Hire Confidence tab.
    const matchCriteria = useMemo(() => {
        if (props.criteria) {
            return props.criteria;
        }
        if (candidate || position) {
            return { ...(candidate || {}), ...(position || {}) };
        }
        return {};
    }, [props.criteria, candidate, position]);

    const showMatchDetails = () => {
        const criteria = matchCriteria;
        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            content: (
                <TalentMatchDetails
                    talent={candidate}
                    projectId={project?.id}
                    positionId={position?.id}
                    isEngagementLead={false}
                    criteria={criteria}
                />
            )
        })
    }

    if (candidate.id === 5336) {
        console.log(candidate, 'TALENT 5336')
    }

    const isHighlighted = candidate.shortlistedAccepted() || candidate.hasProposalForClient();

    return (
        <article
            className={TB.mapClass(
                // The lift on hover is what tells a client the whole tile is a
                // thing they can act on, not just the buttons inside it.
                "bg-white border border-[#E2E3EA] shadow-sm hover:shadow-[0_2px_4px_rgba(14,14,18,0.05),0_16px_36px_rgba(14,14,18,0.12)] hover:-translate-y-px flex flex-col transition-all duration-200 flex-1 h-full rounded-2xl overflow-hidden group relative",
                {
                    'border-primary': candidate.shortlistedAccepted(),
                    'border-primary-lightest': !candidate.shortlistedAccepted() && candidate.hasProposalForClient(),
                    '!border-red-400': candidate.declinedPosition(),
                }
            )}
        >
            <HeaderRow talent={candidate} />

            {/* ACTIONS ROW */}
            <ActionRow
                isDisabled={props.isDisabled}
                shortlistView={props.shortlistView}
                isShortlistPage={isShortlistPage}
                onCandidateAction={onCandidateAction}
            />


            {/* TALENT INFO ROW */}
            <div className="flex flex-col items-center text-center px-[22px] pb-3">

                <Link
                    to={profileLink}
                    className={[
                        'relative flex h-[128px] w-[128px] mb-2.5 shrink-0 items-center justify-center rounded-full',
                        'border border-white transition-shadow duration-200',
                        'shadow-[0_0_0_1px_#E2E3EA,0_6px_18px_rgba(0,0,0,0.09)]',
                        'hover:shadow-[0_0_0_2px_#5F3DB1,0_6px_14px_rgba(95,61,177,0.15)]',
                        '[&>div]:!h-full [&>div]:!w-full',
                        '[&_img]:!h-full [&_img]:!w-full [&_img]:!max-w-none [&_img]:!object-cover [&_img]:!rounded-full',
                    ].join(' ')}
                >
                    <UserImageCircleNEW
                        user={candidate}
                        statusImage={candidate.statusImage()}
                        className="text-4xl"
                    // showAvailabilityDot={false}
                    />
                </Link>

                <TalentDisplayName talent={candidate}>
                    <p className="mb-0 max-w-full line-clamp-1 text-[16.5px] font-bold tracking-[-0.3px] text-[#0E0E14] whitespace-nowrap text-ellipsis transition-colors hover:text-primary">
                        {candidate.firstNamePlusInitial()}
                        {!props.shortlistView && candidate.is_new && <Badge count="NEW" className="ml-2" status="error" />}
                    </p>
                </TalentDisplayName>

                {/* Job title, location and discipline are each optional, but
                    their space is not: a candidate missing one would otherwise
                    stand shorter than the tiles beside it and knock the whole
                    row out of alignment. */}
                <p className="mt-[5px] mb-0 max-w-full min-h-[17px] line-clamp-1 text-[12.5px] font-medium leading-[1.3] text-[#595D68]">
                    {candidate.fullJobTitle()}
                </p>

                <div className="mt-[9px] flex min-h-[15px] items-center">
                    {candidate.location && (
                        <p className="mb-0 inline-flex items-center gap-1 text-[11.5px] font-medium leading-none text-[#8B8F9A]">
                            <i className="fa fa-map-marker-alt" />
                            {candidate.location.city}{!!candidate.location.state && `, ${candidate.location.state}`}
                        </p>
                    )}
                </div>

                <div className="mt-2 flex min-h-[24px] max-w-full items-center">
                    {TB.first(matchingDisciplines) && (
                        <p className="mb-0 inline-flex max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-full border-[1.5px] border-primary bg-white py-[3px] px-[11px] text-[11.5px] font-semibold leading-none text-primary">
                            {TB.first(matchingDisciplines).name}
                        </p>
                    )}
                </div>
            </div>


            {/* SUMMARY ROW */}
            <div className="text-center px-[22px] pb-2.5">
                {/* Fixed height so a one-line bio and a two-line bio don't
                    stagger the score panels across a row of tiles. */}
                <p className="mb-0 h-[38px] line-clamp-2 text-[12.5px] leading-[1.5] text-[#595D68]">
                    {candidate.fullBio() || ''}
                </p>

                <Link to={profileLink} className="mt-1.5 inline-block text-[11.5px] font-semibold text-primary no-underline hover:underline hover:underline-offset-[3px]">
                    View Profile →
                </Link>
            </div>


            {/* MATCH ROW */}
            {/* The tab body is height-locked so a locked Hire Confidence panel
                and a scored one line up across tiles in a grid, rather than
                each tile pulling its rate row to a different height.

                The lock is per tab: Role Match needs far less room than Hire
                Confidence, so rather than pad the short tab out to the tall
                one, each gets its own height and the box animates between
                them. Every tile shares the tab selection, so they all grow and
                shrink together and stay aligned throughout. */}
            <div className="flex flex-col pt-2 px-[5px] pb-3 border-t border-[#EDEEF2]">
                <Tabs
                    activeKey={activeTab}
                    centered
                    // Antd's stock tab chrome — 14px sentence case, brand-purple
                    // label, thick sliding ink bar — competes with the score it
                    // sits above. Pared back to a quiet uppercase label and a
                    // hairline rule so the number stays the focal point.
                    className={[
                        '[&_.ant-tabs-nav:before]:!border-none',
                        '[&_.ant-tabs-tab]:!py-0.5 [&_.ant-tabs-tab]:!px-0',
                        '[&_.ant-tabs-tab+.ant-tabs-tab]:!ml-4',
                        '[&_.ant-tabs-tab]:!text-xxs [&_.ant-tabs-tab]:!font-semibold',
                        '[&_.ant-tabs-tab]:!uppercase [&_.ant-tabs-tab]:!tracking-[0.09em]',
                        '[&_.ant-tabs-tab_.ant-tabs-tab-btn]:!text-[#8B8F9A]',
                        '[&_.ant-tabs-tab-active_.ant-tabs-tab-btn]:!text-[#0E0E14]',
                        // A client sees one tab, so none of the tab chrome
                        // applies: no underline marking a selection that can't
                        // change, and no gap below the nav, leaving the label
                        // to read as a heading over the panel.
                        //
                        // Emitted as either/or rather than layered overrides —
                        // `mb-0` and `mb-[11px]` tie on specificity, so both
                        // present would leave source order to decide.
                        canSeeHireConfidence
                            ? '[&_.ant-tabs-nav]:!mb-[11px] [&_.ant-tabs-ink-bar]:!h-px [&_.ant-tabs-ink-bar]:!bg-[#0E0E14]'
                            : '[&_.ant-tabs-nav]:!mb-0 [&_.ant-tabs-ink-bar]:!hidden',
                        // The height lives on `.ant-tabs-content` rather than
                        // on the panels themselves: that element survives the
                        // tab switch, so the browser has something continuous
                        // to animate. A height set on each pane would swap
                        // instantly instead, which is the jump we're avoiding.
                        '[&_.ant-tabs-content]:!transition-[height] [&_.ant-tabs-content]:!duration-300 [&_.ant-tabs-content]:!ease-out',
                        '[&_.ant-tabs-tabpane]:!h-full',
                        // Each tab is locked to the tallest thing it can hold,
                        // so tiles stay aligned whether or not a candidate has
                        // badges, sub-scores or a beta row. Role Match runs
                        // taller for staff, who also get the AI-Weighted Match
                        // row; add ~25px to both if Profile Skills &
                        // Experience is uncommented in CandidateRoleMatch.
                        activeTab === 'hire-confidence'
                            ? '[&_.ant-tabs-content]:!h-[215px]'
                            : canSeeHireConfidence
                                ? '[&_.ant-tabs-content]:!h-[200px]'
                                : '[&_.ant-tabs-content]:!h-[140px]',
                        // Nothing to switch to, so nothing to click.
                        canSeeHireConfidence ? '' : '[&_.ant-tabs-nav]:!pointer-events-none',
                    ].join(' ')}
                    onChange={setActiveTab}
                    items={[
                        {
                            // Staff see the blended Role Match as a beta row
                            // inside this tab, so the ring here is named for
                            // what it actually shows: the profile-side match.
                            label: canSeeHireConfidence ? `Profile Match` : `Role Match`,
                            key: 'role-match',
                            children: (
                                <FitPanel>
                                    <CandidateRoleMatch
                                        candidate={candidate}
                                        onShowMatchDetails={showMatchDetails}
                                        isActive={activeTab === 'role-match'}
                                    />
                                </FitPanel>
                            ),
                        },
                        ...(canSeeHireConfidence ? [{
                            label: `Hire Confidence`,
                            key: 'hire-confidence',
                            // `isActive` replays the ring and bar fills on each
                            // visit — antd keeps the hidden pane mounted, so
                            // without it they animate once and never again.
                            children: (
                                <FitPanel>
                                    <CandidateHireConfidence
                                        candidate={candidate}
                                        profileLink={profileLink}
                                        criteria={matchCriteria}
                                        isActive={activeTab === 'hire-confidence'}
                                    />
                                </FitPanel>
                            )
                        }] : []),
                    ]}
                />
            </div>

            {/* RATE ROW & CTA ROW*/}
            <div className={TB.mapClass("pt-3 px-[22px] pb-3.5 flex-1 flex flex-col items-center gap-2", {
                'bg-[#F6F4FD]': isHighlighted,
                'bg-[#F5F5F7]': !isHighlighted,
            })}>
                <RateRow talent={candidate} />

                <CTARow {...props} isShortlistPage={isShortlistPage} isTalentPoolPage={isTalentPoolPage} isBrowsePage={isBrowsePage} />
            </div>

        </article>
    )
}

export default CandidateTileRedesign;

/**
 * A shimmering placeholder block.
 *
 * The radius is a prop rather than a class in `className`, because the config
 * sets `important: true` — so a base `rounded` here and a `rounded-full` from
 * the caller tie on specificity and the corners come out square. Only one
 * radius utility is ever emitted.
 */
const Bar = ({ className, radius = 'rounded' }) => (
    <div className={`skeleton ${radius} ${className || ''}`} />
);

/**
 * The loading state for a tile.
 *
 * Deliberately traces the real card's anatomy — same shell, same section
 * padding, same block sizes and positions — so the layout doesn't jump when
 * data lands and the wait reads as "this card is arriving" rather than as a
 * different component.
 */
const CandidateTileRedesignSkeleton = ({ children }) => (
    <article className="bg-white border border-[#E2E3EA] shadow-sm flex flex-col flex-1 h-full rounded-2xl overflow-hidden">

        {/* STATE BAR */}
        <div className="min-h-[34px] bg-gradient-to-b from-[#F4F5F9] to-[#EEF0F5] border-b border-[#EDEEF2]" />

        {/* ACTIONS ROW */}
        <div className="flex justify-between items-start pt-2 px-2.5 min-h-[34px]">
            <div className="flex-1" />
            <Bar radius="rounded-full" className="h-[18px] w-[86px] self-center" />
            <div className="flex justify-end flex-1 gap-1.5">
                <Bar radius="rounded-lg" className="h-[30px] w-[30px]" />
                <Bar radius="rounded-lg" className="h-[30px] w-[30px]" />
            </div>
        </div>

        {/* TALENT INFO ROW */}
        <div className="flex flex-col items-center px-[22px] pb-3">
            <Bar radius="rounded-full" className="h-[128px] w-[128px] mb-2.5" />
            <Bar className="h-[17px] w-1/2 mt-[3px] mb-[6px]" />
            <Bar className="h-3 w-3/4 mt-[7px]" />
            <Bar className="h-3 w-2/5 mt-[11px]" />
            <Bar radius="rounded-full" className="h-[25px] w-3/5 mt-2" />
        </div>

        {/* SUMMARY ROW */}
        <div className="flex flex-col items-center px-[22px] pb-5">
            <div className="h-[38px] w-full flex flex-col items-center gap-1.5">
                <Bar className="h-3 w-full" />
                <Bar className="h-3 w-4/5" />
            </div>
            <Bar className="h-3 w-1/4 mt-1.5" />
        </div>

        {/* MATCH ROW */}
        <div className="flex flex-col pt-2 px-[22px] pb-6 border-t border-[#EDEEF2]">
            <div className="flex justify-center gap-4 mb-[11px]">
                <Bar className="h-4 w-[72px]" />
                <Bar className="h-4 w-[92px]" />
            </div>
            <div className="flex flex-col items-center justify-center min-h-[170px] pt-3 gap-[5px]">
                <Bar radius="rounded-full" className="h-[72px] w-[72px]" />
                <Bar className="h-[17px] w-2/5 mt-1" />
                <Bar className="h-3.5 w-3/4 mt-1" />
                <Bar className="h-3.5 w-1/2" />
                <Bar className="h-3.5 w-[128px] mt-3" />
            </div>
        </div>

        {/* RATE ROW & CTA ROW */}
        <div className="bg-[#F5F5F7] pt-3 px-[22px] pb-3.5 flex-1 flex flex-col items-center gap-2">
            <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
                <Bar className="h-2.5 w-1/3" />
                <Bar className="h-[26px] w-2/5 mt-1" />
            </div>

            {children || (
                <div className="flex items-center flex-1 min-h-[70px] w-full">
                    <Bar radius="rounded-[10px]" className="h-[46px] w-full" />
                </div>
            )}
        </div>
    </article>
);

export { CandidateTileRedesignSkeleton };

/**
 * Fills the height that `.ant-tabs-content` sets for the active tab.
 *
 * The size itself is set out there, not here — a height on this element would
 * be swapped rather than transitioned, because each tab renders its own copy.
 *
 * `overflow-hidden` is the guarantee: without it, a panel whose content runs
 * long spills past the locked height and the tile beside it no longer lines
 * up. Clipping keeps every tile in a row the same height, whatever it holds.
 */
const FitPanel = ({ children }) => (
    <div className="flex flex-col h-full overflow-hidden">
        {children}
    </div>
);



const ActionRow = ({ onCandidateAction, shortlistView, isShortlistPage, isDisabled }) => {
    const { auth } = useAuth();
    const { project, bulkActions } = useProject();
    const { position } = usePosition();
    const [isClientPreferredRateOpen, setIsClientPreferredRateOpen] = useState(false);
    const {
        candidate,
        showCandidateProposal,
        canArchive,
        removeFromArchive,
        addToArchive,
        canFavorite,
        removeFromLiked,
        addToLiked,
        removeFromFavorites,
        canRunBulkActions,
        onAddToBulkActions,
        isBulkActionsSelected,
        addToFavorites,
        canMessage,
        canInterview,
        isDisabled: isCandidateActionDisabled,
        overrideClientPreferredRate
    } = useCandidate();

    const userLikes = candidate.job_candidate?.likes || [];
    const otherUserLikes = TB.filter(userLikes, like => like.causer?.id !== auth.id);

    const dropdownItems = [
        {
            key: 'favorite',
            icon: <i className={`text-danger mr-2 mb-[3px] fa-heart ${candidate.is_favorite ? 'fas' : 'far'}`} />,
            label: candidate.is_favorite ? 'Remove from saved' : 'Save as Favorite',
            onClick: async () => {
                await TB.wait(200);
                onCandidateAction(candidate.is_favorite ? removeFromFavorites : addToFavorites);
            }
        },
        ...((auth.isClientManager() || auth.isClientConsultant()) ? [
            ...(candidate?.email ? [{
                key: 'email',
                icon: <i className="fas fa-envelope" />,
                label: (
                    <a href={`mailto:${candidate.email}?subject=${encodeURIComponent(`Message from Wripple Client Lead regarding ${position?.title} Opportunity with ${auth.client_organization?.name}`)}`}>
                        {candidate.email}
                    </a>
                ),
            }] : []),
            ...(candidate?.phone ? [{
                key: 'call',
                icon: <i className="fas fa-phone mb-[3px]" />,
                label: <a href={`tel:${candidate.phone}`}>{candidate.phone}</a>,
            }] : []),
            {
                key: 'set-rate',
                icon: <i className="fas fa-dollar-sign mb-[3px]" />,
                label: 'Set Client Preferred Rate',
                onClick: () => setIsClientPreferredRateOpen(true),
                disabled: isDisabled
            },
            ...(candidate?.client_override_hourly_rate ? [{
                key: 'delete-rate',
                icon: <i className="fas fa-eraser mb-[3px]" />,
                label: 'Remove Client Preferred Rate',
                onClick: async () => {
                    const confirm = await TB.alert("warning", "Remove Client Preferred Rate",
                        "Are you sure you want to remove this talent's preferred rate for this client?",
                        [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                    );
                    if (confirm) {
                        return overrideClientPreferredRate(false, project, position);
                    }
                },
                disabled: isDisabled
            }] : [])
        ] : [])
    ];



    return (
        <div className="flex relative justify-between items-start pt-2 px-2.5 min-h-[34px]">
            <div className="flex gap-1.5 flex-1 justify-start items-center">
                {!isShortlistPage && (
                    <>
                        <IconButton
                            title="Remove from this project"
                            className={`hover:!bg-red-50 hover:!text-red-500 ${!!bulkActions?.length && 'invisible'}`}
                            disabled={isDisabled || !!bulkActions?.length}
                            onClick={async () => {
                                const confirm = await TB.alert("warning", "Archive Candidate",
                                    "Are you sure you want to remove this candidate for this project?",
                                    [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                                );

                                if (confirm) {
                                    return onCandidateAction(candidate.wasDisliked() ? removeFromArchive : addToArchive)
                                }
                            }}
                        >
                            <CircleX className={`h-3.5 w-3.5 ${candidate.wasDisliked() ? 'rotate-45' : ''}`} />
                        </IconButton>

                        {canRunBulkActions && !candidate.wasDisliked() && (
                            <div className={`ml-1.5 transition group-hover:visible ${isBulkActionsSelected ? 'visible' : 'invisible'}`}>
                                <Checkbox
                                    checked={isBulkActionsSelected}
                                    onChange={onAddToBulkActions}
                                    className="[&_.ant-checkbox-inner]:scale-150 [&_.ant-checkbox]:h-6[&_.ant-checkbox]:w-6"
                                />
                            </div>
                        )}
                    </>
                )}
            </div>


            {candidate.supplier_client?.id ? (
                <Link to={`/agency-profiles/${candidate.supplier_client.id}`} className="text-[9.5px] font-semibold self-center uppercase tracking-[0.1em] flex gap-1.5 items-center py-0.5 px-[9px] rounded-full border border-[#EFE9F5] text-[#A49CB8] transition-colors hover:border-primary hover:text-primary">
                    Agency: <img src={candidate.supplier_client.logo_path} className="h-5 img-fluid max-w-full" />
                </Link>
            ) : (
                <p className="text-[9.5px] font-semibold uppercase self-center mb-0 py-0.5 px-[9px] tracking-[0.1em] rounded-full border border-[#EFE9F5] text-[#A49CB8]">
                    Freelancer
                </p>
            )}


            <div className={`flex gap-1.5 flex-1 justify-end items-center ${!!bulkActions?.length && 'invisible'}`}>
                {!shortlistView && canFavorite && (!bulkActions?.length || candidate.wasLiked()) && (
                    <Badge count={otherUserLikes?.length ? userLikes.length : 0} size="small">
                        <IconButton
                            disabled={isDisabled || bulkActions?.length > 0}
                            onClick={() => onCandidateAction(
                                candidate.wasLiked() ? removeFromLiked : addToLiked
                            )}
                            title={candidate.wasLiked() ? 'Liked' : 'Like'}
                            className={!!candidate.wasLiked() && '!bg-[#ECFDF3] !text-[#4ADE80]'}
                        >
                            <ThumbsUp className={`h-3.5 w-3.5 ${candidate.wasLiked() ? 'text-[#4ADE80] fill-current' : ''}`} />
                        </IconButton>
                    </Badge>
                )}
                {!bulkActions?.length && !isShortlistPage && (
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        menu={{ items: dropdownItems }}
                    >
                        <IconButton>
                            <EllipsisVertical className="h-3.5 w-3.5" />
                        </IconButton>
                    </Dropdown>
                )}
                {!project.isActive() && isShortlistPage && canMessage && !project?.hasRequestedSOW() && !bulkActions?.length && (
                    <Badge count={candidate.unreadMessageCount()} size="small">
                        <IconButton
                            disabled={bulkActions?.length > 0}
                            onClick={() => showCandidateProposal()}
                        >
                            <i className="far fa-comment-alt text-xs" />
                        </IconButton>
                    </Badge>
                )}
                {!project.isActive() && isShortlistPage && canInterview && !project?.hasRequestedSOW() && !bulkActions?.length && (
                    <Badge count={candidate.hasInterviewUpdateSinceLastViewed() ? '!' : null} size="small">
                        <IconButton
                            disabled={bulkActions?.length > 0}
                            onClick={() => showCandidateProposal('&tabView=interviews')}
                        >
                            <i className="far fa-calendar-alt text-xs" />
                        </IconButton>
                    </Badge>
                )}
            </div>

            {!bulkActions?.length && (auth.isClientManager() || auth.isClientConsultant()) && (
                <ClientPreferredRateModal
                    isDisabled={isDisabled}
                    onClose={setIsClientPreferredRateOpen}
                    isOpen={isClientPreferredRateOpen}
                />
            )}

        </div>
    )
}

const IconButton = ({ className, children, ...props }) => {
    return (
        <button
            // Borderless at rest: three outlined boxes across the top competed
            // with the card's own edge. The hover fill still gives the target
            // a visible boundary at the moment it matters.
            className={`h-[30px] w-[30px] rounded-lg bg-transparent text-[#595D68] flex items-center disabled:pointer-events-none disabled:opacity-60 justify-center border border-transparent transition-all duration-150 hover:bg-[#F1F1F4] hover:text-[#1F2029] hover:-translate-y-[1px] ${className}`}
            {...props}
        >
            {children}
        </button>
    )
}


/**
 * Destructive-but-reversible actions. Sits quiet in brand purple until hovered,
 * then turns red — so the consequence is obvious at the moment of clicking
 * rather than shouting for attention the whole time.
 */
const RemoveChip = ({ children, className, ...props }) => (
    <button
        type="button"
        {...props}
        className={`inline-flex items-center gap-[5px] self-center whitespace-nowrap rounded-full border border-primary-lightest bg-white py-1.5 px-[13px] text-[11.5px] font-semibold text-primary transition-all duration-150 disabled:opacity-50 disabled:pointer-events-none hover:border-red-300 hover:bg-red-50 hover:text-red-500 hover:shadow-[0_1px_3px_rgba(229,69,69,0.15)] ${className || ''}`}
    >
        <X className="text-inherit h-[11px] w-[11px]" strokeWidth={2.5} />
        {children}
    </button>
);

/**
 * The tile's main call to action.
 *
 * `variant` carries the meaning: `primary` is the one thing to do next,
 * `secondary` is available but not being urged, `pending` is a state readout
 * wearing a button's shape so the row doesn't collapse while waiting.
 */
const TileCTA = ({ variant = 'primary', subLabel, children, className, ...props }) => {
    const isPending = variant === 'pending';

    return (
        <button
            type="button"
            {...props}
            disabled={props.disabled || isPending}
            className={TB.mapClass(
                'block w-full rounded-[10px] py-[11px] px-3.5 text-sm font-bold tracking-[-0.1px] text-center transition-all duration-150 disabled:opacity-60 disabled:pointer-events-none',
                {
                    'bg-gradient-to-br from-primary to-[#8B63E7] text-white shadow-[0_2px_6px_rgba(95,61,177,0.28),0_8px_20px_rgba(95,61,177,0.18)] hover:from-primary-dark hover:to-primary hover:shadow-[0_4px_8px_rgba(95,61,177,0.35),0_10px_24px_rgba(95,61,177,0.25)] hover:-translate-y-px active:translate-y-0': variant === 'primary',
                    'bg-white text-[#39178B] border-2 border-primary-dark shadow-[0_1px_2px_rgba(95,61,177,0.18)] hover:bg-[#F5F0FF] hover:border-[#39178B] hover:shadow-[0_2px_8px_rgba(95,61,177,0.25)]': variant === 'secondary',
                    'bg-[#F1ECFB] text-primary shadow-[inset_0_0_0_1px_#E3D8F7] !cursor-default !opacity-100': isPending,
                    [className]: !!className,
                }
            )}
        >
            {children}
            {subLabel && (
                <span className={TB.mapClass('block text-[10.5px] font-medium mt-0.5', {
                    'opacity-[0.88]': variant === 'primary',
                    'text-[#595D68] opacity-80': variant === 'secondary',
                    'text-[#9A8CC4]': isPending,
                })}>
                    {subLabel}
                </span>
            )}
        </button>
    );
};

const CTARow = ({ isDisabled: isActionDisabled, isShortlistPage, isTalentPoolPage, isBrowsePage, ...props }) => {
    const { auth } = useAuth();
    const { project, bulkActions, isProjectUpdating } = useProject();
    const { position } = usePosition();

    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        addToCandidates,
        addToArchive,
        canArchive,
        showCandidateProposal,
        assignCandidate,
        unassignCandidate,
        isDisabled: isPositionActionDisabled
    } = useCandidate();

    const isDisabled = isPositionActionDisabled || isActionDisabled;
    const isActionsDisabled = isDisabled || bulkActions?.length > 0 || isProjectUpdating;

    const canAssignTalentForProject = auth?.canAssignTalentForProject(project);
    const canRequestInterview = auth?.canRequestTalentInterviewForProject(project);
    const canInvite = auth?.canInviteTalentForProject(project);

    const hasBeenAssigned = !!position?.assigned_candidate?.id;
    const isAssigned = position?.assigned_candidate?.id === candidate?.id;

    const onCandidateAction = async (request) => {
        let updatedTalent = props.onRequest ? await props.onRequest(request) : await request();
    };

    if (project.isActive()) return null;


    if (isShortlistPage) {
        return (
            <ShortlistedCandidateActions
                isActionsDisabled={isActionsDisabled}
                onCandidateAction={onCandidateAction}
                isAssigned={isAssigned}
                hasBeenAssigned={hasBeenAssigned}
            />
        )
    }
    if (isTalentPoolPage) {
        return (
            <BiddingPoolCandidateActions
                isActionsDisabled={isActionsDisabled}
                onCandidateAction={onCandidateAction}
                isAssigned={isAssigned}
            />
        )
    }

    // // Assigned
    // if (isAssigned) {
    //     return (
    //         <div className="flex flex-col justify-end gap-2 flex-1 min-h[70px] w-full">
    //             <div className="bg-success border border-success textwhite flex items-center justify-center self-end w-full rounded-[10px] py-2.5">
    //                 Selected for Role
    //             </div>
    //         </div>
    //     );
    // }

    // // Invited states
    // if (candidate.hasBeenInvited()) {
    //     // Shortlisted / accepted
    //     if (candidate.shortlistedAccepted()) {
    //         // keep this section to use for the shortlisted tiles
    //         // return (
    //         //     <div className="flex flex-col gap-2 min-h-[70px] w-full">
    //         //         <div className="flex gap-2">
    //         //             <button
    //         //                 type="button"
    //         //                 className={TB.mapClass('flex-1 border rounded-[10px] py-2.5 text-xs', {
    //         //                     'bg-[#F1ECFB] text-primary': candidate.job_candidate?.has_proposed_interview,
    //         //                     'bg-primary text-white': !candidate.job_candidate?.has_proposed_interview,
    //         //                 })}
    //         //                 disabled={isActionsDisabled || !candidate.job_candidate?.can_request_interview || (!isAssigned && hasBeenAssigned)}
    //         //                 onClick={() => {
    //         //                     if (!canRequestInterview) return TB.showPermissionError();
    //         //                     return showCandidateProposal('&tabView=interviews');
    //         //                 }}
    //         //             >
    //         //                 {candidate.job_candidate?.has_proposed_interview ? 'Interview Requested' : 'Request Interview'}
    //         //                 {!canRequestInterview && <i className="fa fa-lock ml-1" />}
    //         //             </button>
    //         //             {!hasBeenAssigned && (
    //         //                 <button
    //         //                     type="button"
    //         //                     className="flex-1 bg-primary border border-primary text-white rounded-[10px] py-2.5 text-xs disabled:opacity-50"
    //         //                     disabled={isActionsDisabled || !candidate.hasSubmittedProposal()}
    //         //                     onClick={() => {
    //         //                         if (!canAssignTalentForProject) return TB.showPermissionError();
    //         //                         return onCandidateAction(() => assignCandidate());
    //         //                     }}
    //         //                 >
    //         //                     Select as Hire
    //         //                     {!canAssignTalentForProject && <i className="fa fa-lock ml-1" />}
    //         //                 </button>
    //         //             )}
    //         //         </div>
    //         //         <button
    //         //             type="button"
    //         //             className="border text-gray-600 self-stretch w-full rounded-[10px] py-2 text-sm"
    //         //             disabled={isActionsDisabled}
    //         //             onClick={() => {
    //         //                 if (!canInvite) return TB.showPermissionError();
    //         //                 return onCandidateAction(removeFromCandidates);
    //         //             }}
    //         //         >
    //         //             Remove from Shortlist
    //         //             {!canInvite && <i className="fa fa-lock ml-1" />}
    //         //         </button>
    //         //     </div>
    //         // );
    //         return (
    //             <div className="flex gap-2 justify-between flex-1 items-center min-h[70px] w-full">
    //                 <div className="inline-flex items-center gap-1.5 text-xs font-bold">
    //                     <div className="flex items-center justify-center h-[18px] w-[18px] shrink-0 bg-primary rounded-full">
    //                         <Check className="text-white h-3.5 w-3.5" />
    //                     </div>

    //                     <span>Shortlisted</span>
    //                 </div>

    //                 <button
    //                     type="button"
    //                     className="text-xxs flex items-center gap-1.5 disabled:opacity-50 rounded-full text-primary bg-white border border-primary-lightest py-1.5 px-3 hover:text-red-500 hover:border-red-400 hover:bg-red-50 transition"
    //                     disabled={isActionsDisabled}
    //                     onClick={async () => {
    //                         if (!canInvite) return TB.showPermissionError();

    //                         const confirm = await TB.alert("warning", "Remove from Shortlist",
    //                             "Are you sure you want to remove this candidate from the shortlist for this project?",
    //                             [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
    //                         );

    //                         if (confirm) {
    //                             return onCandidateAction(removeFromCandidates);
    //                         }
    //                     }}
    //                 >
    //                     <X className="text-inherit h-3 w-3" /> Remove from Shortlist
    //                 </button>
    //             </div>
    //         )
    //     }

    //     // Pending (awaiting proposal)
    //     if (candidate.isPendingShortlisted() && !candidate.shortlistedAccepted()) {
    //         return (
    //             <div className="flex flex-col gap-2 flex-1 min-h[70px] w-full">
    //                 <div className="relative bg-[#F1ECFB] border flex flex-col items-center justify-center text-primary self-stretch w-full py-3 rounded-[10px]">
    //                     <span>Invitation Sent</span>
    //                     <span className="text-xxs block mt-0.5 text-gray-400">Awaiting Proposal</span>

    //                     <button
    //                         type="button"
    //                         disabled={isActionsDisabled}

    //                         onClick={async () => {
    //                             if (!canInvite) return TB.showPermissionError();

    //                             const confirm = await TB.alert("warning", "Cancel Invite",
    //                                 "Are you sure you want to cancel this candidate's proposal invitation for this project?",
    //                                 [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
    //                             );

    //                             if (confirm) {
    //                                 return onCandidateAction(removeFromCandidates);
    //                             }
    //                         }}
    //                         className="p-0.5 absolute top-1 right-1"
    //                     >
    //                         <CircleX className="h-3.5 w-3.5" />
    //                     </button>
    //                 </div>
    //             </div>
    //         );
    //     }

    //     // Declined
    //     if (candidate.declinedPosition()) {
    //         return (
    //             <div className="flex gap-2 justify-between flex-1 items-center min-h[70px] w-full">
    //                 <div className="inline-flex items-center gap-1.5 text-xs font-bold">
    //                     <div className="flex items-center justify-center h-[18px] w-[18px] shrink-0 bg-red-500 rounded-full">
    //                         <X className="text-white h-2.5 w-2.5" />
    //                     </div>

    //                     <span>Not Interested</span>
    //                 </div>

    //                 <button
    //                     type="button"
    //                     className="text-xxs flex items-center gap-1.5 disabled:opacity-50 rounded-full text-primary bg-white border border-primary-lightest py-1.5 px-3 hover:text-red-500 hover:border-red-400 hover:bg-red-50 transition"
    //                     disabled={isActionsDisabled}
    //                     onClick={async () => {
    //                         if (!canInvite) return TB.showPermissionError();

    //                         const confirm = await TB.alert("warning", "Remove & Archive Candidate",
    //                             "Are you sure you want to remove and archive this candidate from this project?",
    //                             [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
    //                         );

    //                         if (confirm) {
    //                             return onCandidateAction(removeFromCandidates);
    //                         }
    //                     }}
    //                 >
    //                     <X className="text-inherit h-3 w-3" /> Remove & Archive
    //                 </button>
    //             </div>
    //         )
    //     }
    // }

    // // Default: not yet invited
    // return (
    //     <div className="flex items-center flex-1 min-h[70px] w-full">
    //         <button
    //             type="button"
    //             disabled={isActionsDisabled}
    //             className="bgprimary btn btn-primary transition flex flex-col disabled:opacity-50 border border-primary text-white self-end w-full rounded-[10px] py-2.5"
    //             onClick={() => {
    //                 if (!permissionToInvite) return TB.showPermissionError();
    //                 return onCandidateAction(addToCandidates);
    //             }}
    //         >
    //             <span className="relative text-sm">
    //                 {candidate.matchStatusIsInterested() ? 'Add To Shortlist' : 'Invite To Propose'}
    //                 {!permissionToInvite && <i className="fa fa-lock absolute right-[-20px] top-[3px]" />}
    //             </span>
    //             <span className="text-xxs block mt0.5 text-white/70">
    //                 {candidate.matchStatusIsInterested() ? '(candidate will be notified)' : '(candidate will be contacted to review brief)'}
    //             </span>
    //         </button>
    //     </div>
    // );
}


/**
 * The rate label. Turns purple once the figure is something the candidate
 * proposed rather than a list price, so a negotiated number never reads as a
 * standard one.
 */
const RateLabel = ({ children, isProposed }) => (
    <p className={TB.mapClass("mb-0 text-xxs font-bold uppercase tracking-[0.12em]", {
        'text-primary': isProposed,
        'text-[#595D68]': !isProposed,
    })}>
        {children}
    </p>
);

const RateValue = ({ children, isProposed }) => (
    <span className={TB.mapClass("text-[26px] font-extrabold tabular-nums tracking-[-0.6px] leading-none", {
        'text-[#39178B]': isProposed,
        'text-[#0E0E14]': !isProposed,
    })}>
        {children}
    </span>
);

const RatePer = ({ children }) => (
    <span className="ml-px text-sm font-semibold text-[#595D68]">{children}</span>
);

const PendingAmount = () => (
    <span className="text-[26px] font-extrabold italic leading-none text-[#8B8F9A]">Pending</span>
);

/**
 * A zero figure is never a real price — it means the proposal hasn't been
 * filled in, or a rate failed to calculate. Showing "$0" invites a client to
 * treat free work as an offer, so these render as Pending instead.
 *
 * Values arrive pre-formatted ("$91", "$1.63k"), hence parsing the digits back
 * out rather than checking the raw number.
 */
const isZeroAmount = (value) => {
    if (value === null || !TB.isDefined(value)) {
        return true;
    }

    const numeric = String(value).replace(/[^0-9.]/g, '');

    return !numeric.length || Number(numeric) === 0;
};

const RateRow = ({ talent }) => {
    const { auth } = useAuth();
    const { position } = usePosition();

    const isFixedType = talent.isFixedFee() || talent.isFixedRetainer();

    // Assigned: show accepted cost/rate
    if (talent.isAssigned()) {
        const acceptedAmount = isFixedType ? talent.proposedPlatformCost() : talent.rangedPlatformRates();

        return (
            <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
                <RateLabel isProposed>
                    {isFixedType ? 'Accepted Cost' : 'Accepted Rate'}
                </RateLabel>
                {!auth.canViewTalentPricing() ? (
                    <span className="text-lg font-bold"><HideTalentPricingTooltip /></span>
                ) : isZeroAmount(acceptedAmount) ? (
                    <PendingAmount />
                ) : (
                    <RateValue isProposed>
                        {acceptedAmount}
                        {!isFixedType && <RatePer>/hr</RatePer>}
                    </RateValue>
                )}
            </div>
        );
    }

    // Fixed fee/retainer with proposal submitted
    if (isFixedType && talent.hasProposalForClient() && !talent.statusIsPending()) {
        let rateDisplay;
        if (!auth.canViewTalentPricing()) {
            rateDisplay = <span className="text-lg font-bold"><HideTalentPricingTooltip /></span>;
        } else if (talent.isFixedRetainer()) {
            const perPeriod = talent.hasSubmittedProposal() && talent.isFixedRetainerProposalValid(position?.isFixedRetainerRateBased())
                ? talent.proposedPlatformPayoutPerPeriod(position?.isFixedRetainerRateBased())
                : null;

            rateDisplay = isZeroAmount(perPeriod) ? <PendingAmount /> : (
                <RateValue isProposed>
                    {perPeriod}
                    <RatePer>/{position?.payout_frequency === 'weekly' ? 'wk' : 'mth'}</RatePer>
                </RateValue>
            );
        } else {
            const fixedFee = talent.hasSubmittedProposal() ? talent.proposedPlatformCost() : null;

            rateDisplay = isZeroAmount(fixedFee)
                ? <PendingAmount />
                : <RateValue isProposed>{fixedFee}<RatePer>fixed fee</RatePer></RateValue>;
        }

        return (
            <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
                <RateLabel isProposed>Proposed Cost</RateLabel>
                {rateDisplay}
            </div>
        );
    }

    // Default: standard/preferred/proposed hourly rate
    const hasProposedChange = talent.isTimeMaterials() && talent.changedHourlyRate();
    const label = hasProposedChange ? 'Proposed Rate' : (
        talent.client_override_hourly_rate ? 'Preferred Rate' : 'Standard Rate'
    );

    const hourlyRate = hasProposedChange ? talent.proposedPlatformRate() : talent.standardPlatformRate();

    return (
        <div className="flex flex-col items-center justify-center gap-[3px] w-full min-h-[52px]">
            <RateLabel isProposed={hasProposedChange}>{label}</RateLabel>
            {!auth.canViewTalentPricing() ? (
                <span className="text-lg font-bold"><HideTalentPricingTooltip /></span>
            ) : isZeroAmount(hourlyRate) ? (
                <PendingAmount />
            ) : (
                <div className="relative flex w-full items-baseline justify-center">
                    {/* The old rate sits beside the new one rather than above
                        it, so the discount reads as a single before/after. */}
                    {hasProposedChange && !isZeroAmount(talent.standardPlatformRate()) && (
                        <span className="absolute right-[calc(50%_+_58px)] top-1/2 -translate-y-1/2 whitespace-nowrap text-[14.4px] font-semibold tabular-nums text-[#8B8F9A] line-through decoration-[1.5px]">
                            {talent.standardPlatformRate()}/hr
                        </span>
                    )}
                    <RateValue isProposed={hasProposedChange}>
                        {hourlyRate}
                        <RatePer>/hr</RatePer>
                    </RateValue>
                </div>
            )}
        </div>
    );
}

const HeaderRow = ({ talent }) => {
    const { showCandidateProposal } = useCandidate();

    const isDeclined = talent.declinedPosition();
    const isShortlisted = talent.shortlistedAccepted();
    const hasProposal = talent.hasProposalForClient();
    const isHighlighted = isShortlisted || hasProposal;

    return (
        <div className={TB.mapClass("min-h-[34px] flex relative items-center justify-between py-2 px-3.5 text-xs font-semibold", {
            // Shortlisted is the deeper, more "settled" gradient; a fresh
            // proposal is the brighter one — the state reads at a glance from
            // across a grid without needing to parse the label.
            'bg-gradient-to-br from-[#39178B] to-primary text-white': isShortlisted,
            'bg-gradient-to-br from-primary to-[#8B63E7] text-white': hasProposal && !isShortlisted,
            'bg-gradient-to-b from-[#F4F5F9] to-[#EEF0F5] border-b border-[#EDEEF2]': !isHighlighted && !isDeclined,
            'bg-red-500 text-white': isDeclined,
        })}>
            {isHighlighted && (
                <>
                    <span>
                        {isShortlisted ? 'Shortlisted' : 'Proposal Submitted'}
                    </span>

                    <button
                        type="button"
                        className="text-[11px] font-semibold text-white cursor-pointer transition-colors flex items-center gap-[3px] py-[3px] pl-[9px] pr-2 bg-white/[0.14] hover:bg-white/[0.28] rounded-full"
                        onClick={() => showCandidateProposal()}
                    >
                        View Proposal <ChevronRight className="text-inherit h-2.5 w-2.5 inline" strokeWidth={2.5} />
                    </button>
                </>
            )}

            {isDeclined && (
                <span className="text-center w-full">Not Interested</span>
            )}
        </div>
    );
}

const ShortlistedCandidateActions = ({ isActionsDisabled, onCandidateAction, isAssigned, hasBeenAssigned, ...props }) => {
    const { auth } = useAuth();
    const { project } = useProject();
    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        addToCandidates,
        addToArchive,
        canArchive,
        showCandidateProposal,
        assignCandidate,
        unassignCandidate,
        // isDisabled: isPositionActionDisabled
    } = useCandidate();

    const canRequestInterview = auth?.canRequestTalentInterviewForProject(project);
    const canAssignTalentForProject = auth?.canAssignTalentForProject(project);
    const canInvite = auth?.canInviteTalentForProject(project);



    // TODO:  need to figure out bottom area for shortlisted candidate action

    // Pending (awaiting proposal)
    if (candidate.isPendingShortlisted() && !candidate.shortlistedAccepted()) {
        return (
            <div className="relative flex flex-col gap-2 flex-1 min-h-[70px] w-full justify-center">
                <TileCTA
                    variant="pending"
                    subLabel={`Awaiting ${candidate.first_name || 'their'}'s proposal`}
                >
                    Invitation Sent
                </TileCTA>

                <button
                    type="button"
                    title="Cancel invite"
                    disabled={isActionsDisabled}
                    onClick={async () => {
                        if (!canInvite) return TB.showPermissionError();

                        const confirm = await TB.alert("warning", "Cancel Invite",
                            "Are you sure you want to cancel this candidate's proposal invitation for this project?",
                            [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                        );

                        if (confirm) {
                            return onCandidateAction(removeFromCandidates);
                        }
                    }}
                    className="p-0.5 absolute top-1 right-1 text-primary/60 hover:text-primary transition-colors"
                >
                    <CircleX className="h-3.5 w-3.5" />
                </button>
            </div>
        );
    }


    // Declined
    if (candidate.declinedPosition()) {
        return (
            <StatusFooter
                label="Not Interested"
                tone="danger"
                icon={<X className="text-white h-2.5 w-2.5" strokeWidth={3.5} />}
            >
                <RemoveChip
                    disabled={isActionsDisabled}
                    onClick={async () => {
                        if (!canInvite) return TB.showPermissionError();

                        const confirm = await TB.alert("warning", "Remove & Archive Candidate",
                            "Are you sure you want to remove and archive this candidate from this project?",
                            [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                        );

                        if (confirm) {
                            return onCandidateAction(removeFromCandidates);
                        }
                    }}
                >
                    Remove From Shortlist
                </RemoveChip>
            </StatusFooter>
        )
    }


    return (
        <div className="flex flex-col gap-2 min-h-[70px] w-full">
            {/* Select as Hire is the decisive action, so it keeps the filled
                gradient; requesting an interview is a step along the way. */}
            <div className="flex gap-2">
                <TileCTA
                    variant={candidate.job_candidate?.has_proposed_interview || !hasBeenAssigned ? 'secondary' : 'primary'}
                    className="!flex-1 !py-2.5 !text-xs"
                    disabled={isActionsDisabled || !candidate.job_candidate?.can_request_interview || (!isAssigned && hasBeenAssigned)}
                    onClick={() => {
                        if (!canRequestInterview) return TB.showPermissionError();
                        return showCandidateProposal('&tabView=interviews');
                    }}
                >
                    {candidate.job_candidate?.has_proposed_interview ? 'Interview Requested' : 'Request Interview'}
                    {!canRequestInterview && <i className="fa fa-lock ml-1" />}
                </TileCTA>
                {!hasBeenAssigned && (
                    <TileCTA
                        className="!flex-1 !py-2.5 !text-xs"
                        disabled={isActionsDisabled || !candidate.hasSubmittedProposal()}
                        onClick={() => {
                            if (!canAssignTalentForProject) return TB.showPermissionError();
                            return onCandidateAction(() => assignCandidate());
                        }}
                    >
                        Select as Hire
                        {!canAssignTalentForProject && <i className="fa fa-lock ml-1" />}
                    </TileCTA>
                )}
            </div>

            <RemoveChip
                disabled={isActionsDisabled}
                onClick={async () => {
                    if (!canInvite) return TB.showPermissionError();

                    const confirm = await TB.alert("warning", "Remove from Shortlist",
                        "Are you sure you want to remove this candidate from the shortlist for this project?",
                        [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                    );

                    if (confirm) {
                        return onCandidateAction(removeFromCandidates);
                    }
                }}
            >
                Remove from Shortlist
            </RemoveChip>

            {/* <button
                type="button"
                className="border text-gray-600 self-stretch w-full rounded-[10px] py-2 text-sm"
                disabled={isActionsDisabled}
                onClick={() => {
                    if (!canInvite) return TB.showPermissionError();
                    return onCandidateAction(removeFromCandidates);
                }}
            >
                Remove from Shortlist
                {!canInvite && <i className="fa fa-lock ml-1" />}
            </button> */}
        </div>
    );
}

const BiddingPoolCandidateActions = ({ isActionsDisabled, onCandidateAction, isAssigned, ...props }) => {
    const { auth } = useAuth();
    const { project } = useProject();
    const {
        candidate,
        permissionToInvite,
        removeFromCandidates,
        addToCandidates,
        addToArchive,
        canArchive,
        showCandidateProposal,
        assignCandidate,
        unassignCandidate,
        // isDisabled: isPositionActionDisabled
    } = useCandidate();

    // const isDisabled = isPositionActionDisabled || isActionDisabled;
    // const isActionsDisabled = isDisabled || bulkActions?.length > 0 || isProjectUpdating;

    // const canAssignTalentForProject = auth?.canAssignTalentForProject(project);
    // const canRequestInterview = auth?.canRequestTalentInterviewForProject(project);
    const canInvite = auth?.canInviteTalentForProject(project);

    // const hasBeenAssigned = !!position?.assigned_candidate?.id;
    // const isAssigned = position?.assigned_candidate?.id === candidate?.id;


    // Assigned
    if (isAssigned) {
        return (
            <div className="flex flex-col justify-center gap-2 flex-1 min-h-[70px] w-full">
                <div className="bg-success text-[#0E0E14] font-bold text-sm flex items-center justify-center gap-1.5 w-full rounded-[10px] py-[11px]">
                    <Check className="h-4 w-4" strokeWidth={3} />
                    Selected for Role
                </div>
            </div>
        );
    }

    // Invited states
    if (candidate.hasBeenInvited()) {
        // Shortlisted / accepted
        if (candidate.shortlistedAccepted()) {
            return (
                <StatusFooter
                    label="Shortlisted"
                    icon={<Check className="text-white h-2.5 w-2.5" strokeWidth={3.5} />}
                >
                    <RemoveChip
                        disabled={isActionsDisabled}
                        onClick={async () => {
                            if (!canInvite) return TB.showPermissionError();

                            const confirm = await TB.alert("warning", "Remove from Shortlist",
                                "Are you sure you want to remove this candidate from the shortlist for this project?",
                                [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                            );

                            if (confirm) {
                                return onCandidateAction(removeFromCandidates);
                            }
                        }}
                    >
                        Remove from Shortlist
                    </RemoveChip>
                </StatusFooter>
            )
        }

        // Pending (awaiting proposal)
        if (candidate.isPendingShortlisted() && !candidate.shortlistedAccepted()) {
            return (
                <div className="relative flex flex-col gap-2 flex-1 min-h-[70px] w-full justify-center">
                    <TileCTA
                        variant="pending"
                        subLabel={`Awaiting ${candidate.first_name || 'their'}'s proposal`}
                    >
                        Invitation Sent
                    </TileCTA>

                    <button
                        type="button"
                        title="Cancel invite"
                        disabled={isActionsDisabled}
                        onClick={async () => {
                            if (!canInvite) return TB.showPermissionError();

                            const confirm = await TB.alert("warning", "Cancel Invite",
                                "Are you sure you want to cancel this candidate's proposal invitation for this project?",
                                [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                            );

                            if (confirm) {
                                return onCandidateAction(removeFromCandidates);
                            }
                        }}
                        className="p-0.5 absolute top-1 right-1 text-primary/60 hover:text-primary transition-colors"
                    >
                        <CircleX className="h-3.5 w-3.5" />
                    </button>
                </div>
            );
        }

        // Declined
        if (candidate.declinedPosition()) {
            return (
                <StatusFooter
                    label="Not Interested"
                    tone="danger"
                    icon={<X className="text-white h-2.5 w-2.5" strokeWidth={3.5} />}
                >
                    <RemoveChip
                        disabled={isActionsDisabled}
                        onClick={async () => {
                            if (!canInvite) return TB.showPermissionError();

                            const confirm = await TB.alert("warning", "Remove & Archive Candidate",
                                "Are you sure you want to remove and archive this candidate from this project?",
                                [{ title: 'Yes, Continue' }, { title: 'Cancel' }]
                            );

                            if (confirm) {
                                return onCandidateAction(removeFromCandidates);
                            }
                        }}
                    >
                        Remove & Archive
                    </RemoveChip>
                </StatusFooter>
            )
        }
    }

    // Default: not yet invited
    return (
        <div className="flex items-center flex-1 min-h-[70px] w-full">
            <TileCTA
                disabled={isActionsDisabled}
                subLabel={candidate.matchStatusIsInterested()
                    ? 'Candidate will be notified'
                    : 'Candidate will review the brief'}
                onClick={() => {
                    if (!permissionToInvite) return TB.showPermissionError();
                    return onCandidateAction(addToCandidates);
                }}
            >
                <span className="relative">
                    {candidate.matchStatusIsInterested() ? 'Add To Shortlist' : 'Invite To Propose'}
                    {!permissionToInvite && <i className="fa fa-lock absolute right-[-20px] top-[3px]" />}
                </span>
            </TileCTA>
        </div>
    );
}

/**
 * A settled state rather than an action: the outcome on the left, the way to
 * undo it on the right. Keeps the footer the same height as a CTA so tiles in
 * different states still line up.
 */
const StatusFooter = ({ label, icon, tone = 'primary', children }) => (
    <div className="flex gap-2 justify-between flex-1 items-center min-h-[70px] w-full">
        <div className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#39178B]">
            <div className={TB.mapClass("flex items-center justify-center h-[18px] w-[18px] shrink-0 rounded-full", {
                'bg-primary': tone === 'primary',
                'bg-red-500': tone === 'danger',
            })}>
                {icon}
            </div>

            <span>{label}</span>
        </div>

        {children}
    </div>
);